import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Phone, ArrowRight, Loader2, Shield } from 'lucide-react';
import { sendOTP, verifyOTP, mockUser } from '../../data/appMock';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
  const [step, setStep] = useState(1); // 1: phone, 2: OTP
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 8) {
      toast.error('Numéro de téléphone invalide');
      return;
    }
    
    setIsLoading(true);
    try {
      await sendOTP(phone);
      toast.success('Code envoyé !', {
        description: 'Vérifiez vos SMS (Code démo: 123456)'
      });
      setStep(2);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyOTP(phone, otp);
      login(result.user);
      toast.success('Connexion réussie !');
      navigate('/app');
    } catch (error) {
      toast.error('Code invalide');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6B2C91] via-[#5a2579] to-[#4a1f64] flex flex-col">
      {/* Header */}
      <div className="p-6 pt-12 text-center">
        <img
          src="/images/kipay-logo.png"
          alt="KIPAY"
          className="h-20 mx-auto mb-4"
        />
        <h1 className="text-2xl font-bold text-white">Bienvenue sur KIPAY</h1>
        <p className="text-white/70 mt-2">Votre wallet digital EKIP</p>
      </div>

      {/* Form Card */}
      <div className="flex-1 mt-8 bg-white rounded-t-[2rem] p-6">
        {step === 1 ? (
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#6B2C91]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#6B2C91]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Connexion</h2>
              <p className="text-gray-600 text-sm mt-2">
                Entrez votre numéro de téléphone pour recevoir un code de vérification
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de téléphone
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  +225
                </span>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="07 08 09 10 11"
                  className="pl-14 h-14 text-lg"
                  maxLength={10}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 bg-[#6B2C91] hover:bg-[#5a2579] text-white text-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continuer
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#00C853]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#00C853]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Vérification</h2>
              <p className="text-gray-600 text-sm mt-2">
                Entrez le code à 6 chiffres envoyé au<br />
                <span className="font-semibold">+225 {phone}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code de vérification
              </label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="h-14 text-2xl text-center tracking-[0.5em] font-mono"
                maxLength={6}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Code démo : <span className="font-mono font-bold">123456</span>
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 bg-[#6B2C91] hover:bg-[#5a2579] text-white text-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Vérifier
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-[#6B2C91] text-sm font-medium"
            >
              ← Modifier le numéro
            </button>

            <button
              type="button"
              onClick={() => {
                sendOTP(phone);
                toast.success('Code renvoyé !');
              }}
              className="w-full text-gray-500 text-sm"
            >
              Renvoyer le code
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-500">
            En continuant, vous acceptez nos{' '}
            <a href="#" className="text-[#6B2C91]">Conditions d'utilisation</a>
            {' '}et notre{' '}
            <a href="#" className="text-[#6B2C91]">Politique de confidentialité</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
